import { Spinner } from "@aidkitorg/component-library";
import { useContext, useRef, useState, useEffect } from "react";
import { usePost } from "../API";
import { CandidateChooser } from "../Components/CandidateChooser";
import { ValidatorMessage } from "../Components/ValidatorMessage";
import InterfaceContext, { AuthContext } from "../Context";
import { useLocalizedStrings } from "../Localization";
import { languageContent, safeParse, saveKeyToCookieAndRelocateToUrl, useMarkdown } from "../Util";
import { InfoDict, QuestionProps } from "./Props";
import * as v0 from "@aidkitorg/types/lib/survey";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { useCookies } from "react-cookie";
import { trySetPortalToken } from "../Hooks/PortalSession";
import { ApplicationStateContext } from "../Contexts/ApplicationStateContext";

/**
 * Decides what info to hand back to the survey once the applicant confirms their contact.
 *
 * This only shapes the info to *load* (hydrate) into the survey — it does not write the
 * confirmation field. Persisting the confirmation (targetField + _confirmed_info_) is done
 * separately via setInfoKey, which both saves the answer and recomputes step visibility.
 *
 * When resuming an existing app, the server's stored info is the source of truth, so use it
 *   as-is. We don't overlay the current form state — the saved app data wins.
 *
 * For a new app, previousInfo may contain copied prefill from another app.
 *   Apply current form values after it so anything entered this session wins on
 *   key conflicts.
 *
 * @param isNewApp  Whether this confirmation started/created a new app or is resuming an existing one.
 */
export function buildResumeInfoToLoad({
  previousInfo,
  currentInfo,
  isNewApp,
}: {
  previousInfo: InfoDict,
  currentInfo: InfoDict,
  isNewApp: boolean,
}): InfoDict {
  return isNewApp
    ? { ...previousInfo, ...currentInfo }
    : { ...previousInfo };
}

export function ContactConfirmation(props: QuestionProps) {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const sessionStorage = context.sessionStorage;
  // /p/* link key from the cookie useLinkKey writes at page load. Fallback for
  // outerAuthToken since useLinkKey doesn't populate authContext from URL keys.
  const linkKeyCookieName = encodeURIComponent('key:' + window.location.pathname);
  const [linkKeyCookies] = useCookies([linkKeyCookieName]);
  const linkKey: string | undefined = linkKeyCookies[linkKeyCookieName];
  // Screener auth lives in the auth_token cookie; AuthContext isn't provided on /a/* paths.
  const [authTokenCookies] = useCookies(['auth_token']);
  const authTokenCookie: string | undefined = authTokenCookies['auth_token'];

  //const sendConfirmation = useAPIPost('/confirm_contact');
  const sendConfirmation = usePost("/confirm/phone");
  const checkCode = usePost("/confirm/phone_code");
    
  const token = useRef('')
  const resume = usePost("/subsurvey/resume", {
    token: () => {
      return token.current;
    }
  });

  const getApplicantCandidates = usePost("/applicant/get_candidates", { token: () => token.current });
  const [startNewToken, setStartNewToken] = useState('');
  const [showCandidates, setShowCandidates] = useState([] as Awaited<ReturnType<typeof getApplicantCandidates>>['candidates']);
  const [showCandidateChooser, setShowCandidateChooser] = useState(false);
  const [dialogDescription, setDialogDescription] = useState<undefined | v0.RichText>(undefined);
  const dialogDescriptionText = dialogDescription?.[context.lang] || dialogDescription?.['en'];
  const dialogDescriptionMd = useMarkdown(dialogDescriptionText);
  const [homePageLink, setHomePageLink] = useState('');
  
  const formattedContent = useModularMarkdown({
    content: props[languageContent(context.lang)] || '',
    info: props.info,
  });
  
  // Authenticated - No resume and only authorized screener/subsurvey can confirm contact.
  const authOnly = (props["Additional Options"] || []).indexOf('Authenticated') !== -1;
  
  // Get challenges from local window if we are NOT dealing with an auth only confirmation.
  const [challenges, setChallengesState] = useState<Record<string, string> | null>(
    (!authOnly) && sessionStorage.getItem('challenges') ?  JSON.parse(sessionStorage['challenges']) : null);
  const [contact, setContact] = useState<string | null>(sessionStorage.getItem('challenge_contact') || null);
  
  const [code, setCode] = useState('');
  const [validationError, setValidationError] = useState(''); // used for bettering UX
  const checking = useRef(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [checkingCode, setCheckingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const metadata = safeParse(props['Metadata'] || '{}') as Omit<v0.ContactConfirmation, 'content'> & { contact_field: string };
  const codeBox = useRef<HTMLInputElement | null>(null);
  const contact_key = metadata.contact_field || 'phone_number';
  const confirmationType: 'phone' | 'email' = contact_key.includes('email') ? 'email' : 'phone';
  const prefix = confirmationType + ':';

  const [cookies , setCookie] = useCookies(['portal'])
  const { applicationStateChanged } = useContext(ApplicationStateContext);

  let pleaseEnterCode = L.questions.contact_confirmation.please_enter_code_from_sms;

  if ((props.info[contact_key] || '').indexOf('@') !== -1) {
    pleaseEnterCode = L.questions.contact_confirmation.please_enter_code_from_email;
  }

  let pleasePressButtonText = L.questions.contact_confirmation.please_press_button_sms;
  if (metadata.sendCodeViaWhatsapp) {
    pleaseEnterCode = L.questions.contact_confirmation.please_enter_code_from_whatsapp;
    pleasePressButtonText = L.questions.contact_confirmation.please_press_button_whatsapp;
  }

  const isEmail = (props.info[contact_key] || '').includes('@');
  const pleasePressButtonMd = useMarkdown(isEmail ? 
    metadata.customEmailConsentText 
      ? (metadata.customEmailConsentText[context.lang] || metadata.customEmailConsentText.en)
      : L.questions.contact_confirmation.please_press_button_email
    : metadata.customSMSConsentText 
      ? (metadata.customSMSConsentText[context.lang] || metadata.customSMSConsentText.en)
      : pleasePressButtonText);

  const authContext = useContext(AuthContext);

  const [confirmed, setConfirmed] = useState(false);
  
  const allowRedirect = props.Viewer !== 'screener' 
      && !window.location.pathname.startsWith('/config')
      && !window.location.pathname.startsWith('/p/');

  function setChallenges(challenges: Record<string, string> | null) {
    if (!props.uid) { 
      if (challenges) {
        sessionStorage.setItem('challenges', JSON.stringify(challenges));
        sessionStorage.setItem('challenge_contact', props.info[contact_key]);
      } else {
        sessionStorage.removeItem('challenges');
      }
    }
    setChallengesState(challenges);
    setContact(props.info[contact_key]);
  }
  
  // If the contact is different cancel the challenge
  useEffect(() => {
    if (props.info[contact_key] != contact) {
      setChallenges(null);
      setCode('');
    }
  }, [props.info]);
  
  // Keep confirmed state in sync with the contact state
  useEffect(() => {
    if (props.info[props["Target Field"]!]) {
      setConfirmed(true);
    } else {
      setConfirmed(false);
    }
  }, [props.info[props["Target Field"]!]]);
  
  const doResume = async (params: {
    ingested: boolean,
    resumptionToken: string,
    confirmationToken: string,
    decision?: "new" | "resume",
    isNewApp: boolean,
  }) => {
    token.current = params.resumptionToken;
    if (params.decision === 'resume' && allowRedirect) {
      // only alert if we are resuming.
      alert(L.questions.contact_confirmation.previous_application_found);
    }
    if (params.ingested && allowRedirect) {
      const redirectsTo = metadata.ingestedResumesToSubsurvey || ('apply');
      const newURL = `${window.location.protocol}//${window.location.host}/p/${redirectsTo}?key=${token.current}`;
      // alert("Forwarding to " + newURL);
      saveKeyToCookieAndRelocateToUrl(newURL);
      return;
    } else {
      const previous = await resume({
        form_name: 'apply'
      });

      if (previous?.newToken) {
        token.current = previous?.newToken;
      }

      const previousInfo = previous?.info || {};
      const infoToLoad = buildResumeInfoToLoad({
        previousInfo,
        currentInfo: props.info,
        isNewApp: params.isNewApp,
      });

      if (previous.submitted) {
        // return early so we don't set auth in the next block. this application can't be resumed until its ingested.
        props.LoadInfo?.(infoToLoad, token.current, previous.submitted)
        return
      }
      props.LoadInfo?.(infoToLoad, token.current);
      // Persist the confirmation and advance the survey. LoadInfo only hydrates in-memory info;
      if (!previousInfo[props["Target Field"]!]) {
        props.setInfoKey(props["Target Field"]!, prefix + params.confirmationToken, true, false);
        props.setInfoKey(contact_key + '_confirmed_info_', JSON.stringify({ field: props["Target Field"] }), true, false);
      }
    }
    authContext.setToken(token.current);
  }

  const doCheckCode = async (code: string) => {
    setCode(code);
    if (code.length !== 6) {
      setValidationError(L.questions.contact_confirmation.code_is_not_6_digits);
      return;
    }
  
    if (checking.current) return;
    checking.current = true;
    setCheckingCode(true);
    try {
      // Screeners auth via the auth_token cookie (AuthContext isn't provided on /a/*). For everyone
      // else, prefer authContext (post-resume Status reflects the resolved candidate) and fall back to
      // the /p/* link cookie for initial entry.
      const outerAuthToken = props.Viewer === 'screener'
        ? authTokenCookie
        : (authContext.token?.() || linkKey);
      const matched = await checkCode({
        answer: code,
        token: challenges!.phone,
        contact_key,
        uid: props.uid,
        // Authorizes `uid` server-side for .access maintenance: a screener's challenge carries no
        // applicant, so without this their contact change mints/prunes nothing.
        outerAuthToken,
      })
      if (matched.error === 'challenge_expired') {
        setChallenges(null);
        setCode('');
        alert(L.questions.contact_confirmation.code_expired);
      } else if (matched.error === 'challenge_incorrect') {
        alert(L.questions.contact_confirmation.code_incorrect);
      } else if (matched.attestation) {
        setVerifying(true);
        setChallenges(null);
        setCode('');

        token.current = matched.attestation;

        try {
          let shouldSetNewInfo = true;
          if ((props["Additional Options"] || []).indexOf("Don't Resume") === -1) {
            const res = await getApplicantCandidates({
              info: { ...props.info, uid: props.uid || props.info.uid },
              mode: 'confirmation',
              outerAuthToken,
            });

            if (res.portalToken) {
              setCookie('portal', res.portalToken, {path: '/'});
              trySetPortalToken(res.portalToken);
            }

            const { candidates, canStartNewToken, homePageLink } = res;
            setDialogDescription(res.dialogDescription);

            const anyCandidateHasSubsurveys = candidates.some((c) => c.subsurveys?.length);

            if (allowRedirect && candidates.length && (candidates.length > 1 || canStartNewToken || anyCandidateHasSubsurveys)) {
              setStartNewToken(canStartNewToken || '');
              setShowCandidates(candidates);
              setHomePageLink(homePageLink || '');
              return;
            } else if (candidates.length === 1 && !canStartNewToken) {
              return await doResume({
                ingested: candidates[0].applicant.kind === 'ingested',
                resumptionToken: candidates[0].resumeToken,
                confirmationToken: matched.attestation,
                decision: res.decision,
                isNewApp: res.decision === 'new',
              });
            }
          }

          if (shouldSetNewInfo) {
            props.setInfoKey(props["Target Field"]!, prefix + matched.attestation, true, false);
            // The setTimeout is a hack to make sure info is updated correctly by waiting until the first setInfoKey finishes before setting the second
            // TODO: Fix the underlying issue preventing this from setting correctly
            setTimeout(() => {
              props.setInfoKey(contact_key + '_confirmed_info_', JSON.stringify({ field: props["Target Field"] }), true, false);
            }, 0);
          }

          // Only save this as an auth token if we don't already have an auth token.
          if (!authContext.token?.()) {
            authContext.setToken(matched.attestation);
          }
        } catch (e) {
          setVerifying(false);
          throw e;
        }
      } else {
        setChallenges(null);
        setCode('');
        authContext.setToken(null);
      }
    } finally {
      checking.current = false;
      setCheckingCode(false);
    }
  }
  
  const showConfirmation = (challenges || confirmed);

  // If we have ANY candidates, show the candidate chooser
  useEffect(() => {
    setShowCandidateChooser(showCandidates && showCandidates.length > 0);
  }, [showCandidates]);
  
  // Safe to return empty, unlike Computed: nothing here computes in the background.
  if ((props["Additional Options"] || []).indexOf("Hidden") !== -1) {
    return <></>
  }

  if (showCandidateChooser) {
    return <CandidateChooser
      label={<>{dialogDescriptionText ? dialogDescriptionMd : L.apply.we_found_some_existing_apps}
        {startNewToken ? <><br /><span>{L.apply.you_can_also_start_new}</span></> : null}
      </>}
      candidates={[
        ...(homePageLink ? [{
          buttonLabel: L.questions.contact_confirmation.go_to_home_page,
          onClick: async () => {
            setShowCandidates([] as any[]);
            window.location.href = homePageLink;
          }
        }] : []),
        ...showCandidates.map((c) => ({
          ...c, 
          buttonLabel: metadata?.customResumeButtonText?.[context.lang],
          onClick: async () => {
            setShowCandidates([] as any[]);
            await doResume({
              ingested: c.applicant.kind === 'ingested',
              resumptionToken: c.resumeToken,
              confirmationToken: token.current,
              isNewApp: false,
            });
          }
        })), 
        ...(startNewToken ? [{
          buttonLabel: L.apply.start_new_application,
          onClick: async () => {
            setShowCandidates([] as any[]);
            await doResume({
              ingested: false,
              resumptionToken: startNewToken,
              confirmationToken: token.current,
              isNewApp: true,
            });
          }
        }] : [])]} 

      onClose={() => {
        setShowCandidates([] as any[]);
        setVerifying(false);
      }}
    />
  }
  
  return <fieldset>
    <legend>
      {formattedContent}
    </legend>
    <div className="mt-2 flex-col">
      <label style={{display: "block"}} 
        aria-hidden={!showConfirmation ? "true" : "false"} htmlFor="contact-code">
        <span>{confirmed
          ? <span>{L.questions.contact_confirmation.contact_has_been_confirmed}</span>
          : verifying
            ? <Spinner size="xsmall" className="p-0 w-auto" />
            : (challenges ? <span>{pleaseEnterCode}</span> : <span>{pleasePressButtonMd}</span>)
        }</span>
        {!confirmed &&
                <>
                  <input
                    ref={codeBox}
                    autoComplete="one-time-code"
                    value={confirmed ? L.questions.contact_confirmation.contact_confirmed : code}
                    id="contact-code"
                    aria-hidden={!showConfirmation ? "true" : "false"}
                    disabled={!challenges && !confirmed}
                    onChange={(e) => { 
                      const value = (e.target.value || '').replace(/[^0-9]/g, '');
                      if (value.length === 6) {
                        setValidationError('');
                      }
                      setCode(value);
                    }}
                    className=
                      {(showConfirmation ? "" : "hidden ") + "max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md border-2 border-green-200 focus:ring-green-400 ring-green-400 focus:border-green-400"}
                  />
                  {challenges && <div className="text-sm text-gray-600 mt-1 mb-2">{L.questions.contact_confirmation.authentication_code_disclaimer}</div>}
                  {challenges &&
                    <>
                      <div role="alert">
                        {validationError && <ValidatorMessage message={validationError} />}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-60"
                          type="submit" disabled={checkingCode} onClick={(e) => { e.preventDefault(); doCheckCode(code); }}>
                          <span>{checkingCode && <Spinner size="xsmall" className="p-0 w-auto mr-2" />}</span>
                          <span>{L.apply.confirm_code}</span>
                        </button>
                        <button
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                          onClick={(e) => { setCode(''); setChallenges(null); }}>{L.apply.cancel}</button>
                      </div>
                    </>}
                </>}
      </label>
      {!challenges && !confirmed && !verifying &&
            <button
              type="button"
              onClick={async (e) => {
                if (sendingCode) return;
                setSendingCode(true);
                const challenges = await sendConfirmation({
                  phone: props.info[contact_key],
                  language: context.lang,
                  sendCodeViaWhatsapp: metadata.sendCodeViaWhatsapp
                })
                if (challenges && challenges.token) {
                  setChallenges({'phone': challenges.token});
                  setSendingCode(false);
                  setTimeout(() => codeBox.current?.focus(), 100);
                  applicationStateChanged({
                    type: 'change',
                    questionId: props.Question,
                    update: {
                      validity: {
                        state: 'valid'
                      }
                    }
                  });
                } else {
                  applicationStateChanged({
                    type: 'change',
                    questionId: props.Question,
                    update: {
                      validity: {
                        state: 'invalid',
                        message: L.questions.contact_confirmation.send_confirmation_code_failed
                      }
                    }
                  });
                  alert(L.questions.contact_confirmation.send_confirmation_code_failed);
                  setSendingCode(false);
                }
              }}
              className="inline-flex items-center -mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-60"
              disabled={sendingCode}
            >
              <span>{sendingCode && <Spinner size="xsmall" className="p-0 w-auto mr-2" />}</span>
              <span>{metadata?.customSendButtonText?.[context.lang]
                || metadata?.customSendButtonText?.en
                || L.questions.contact_confirmation.send_confirmation_code}</span>
            </button>}
    </div>
  </fieldset>
}
